import React from "react";

/**
 * OracleStatusBadges — Simplicity Release v2.0
 *
 * Public users never see Mongo / LLM / OANDA technical state.
 * AdminOne operators see the full picture by passing `adminMode={true}`.
 *
 * Truthfulness: when degraded we say so plainly — but in user-facing language
 * ("Some live providers pending") rather than leaking infrastructure terms.
 */
export default function OracleStatusBadges({
  oracleOnline = true,
  dataFreshness = "public",
  oandaConnected = false,
  humanReviewRequired = false,
  mongoConnected = true,
  llmConfigured = true,
  adminMode = false,
}) {
  const dfLabel = (dataFreshness || "public").toString().toUpperCase();
  const dfClass =
    dfLabel === "LIVE" ? "is-live"
    : dfLabel === "SIMULATED" ? "is-sim"
    : dfLabel === "NOT CONNECTED" ? "is-off"
    : "is-public";

  // Public view (default): no technical leakage.
  if (!adminMode) {
    const providersPending = !oandaConnected || !llmConfigured;
    return (
      <div className="oracle-status-bar" role="status" aria-live="polite" data-testid="oracle-status-badges-public">
        <span className={`status-badge ${oracleOnline ? "is-on" : "is-off"}`} data-testid="badge-oracle-online">
          <span className="status-dot" /> ORACLE AVAILABLE
        </span>
        <span className={`status-badge ${dfClass}`} data-testid="badge-data-mode">PUBLIC DATA MODE</span>
        {providersPending && (
          <span className="status-badge is-warn" data-testid="badge-providers-pending">
            SOME LIVE PROVIDERS PENDING
          </span>
        )}
        {humanReviewRequired && (
          <span className="status-badge is-hitl" data-testid="badge-hitl">HUMAN REVIEW REQUIRED</span>
        )}
      </div>
    );
  }

  // AdminOne view: full technical detail.
  return (
    <div className="oracle-status-bar" role="status" aria-live="polite" data-testid="oracle-status-badges-admin">
      <span className={`status-badge ${oracleOnline ? "is-on" : "is-off"}`}>
        <span className="status-dot" /> ORACLE ONLINE
      </span>
      <span className={`status-badge ${dfClass}`}>DATA: {dfLabel}</span>
      <span className={`status-badge ${oandaConnected ? "is-on" : "is-warn"}`}>
        {oandaConnected ? "OANDA CONNECTED" : "OANDA READY · KEYS REQUIRED"}
      </span>
      {humanReviewRequired && <span className="status-badge is-hitl">HITL REVIEW REQUIRED</span>}
      <span className={`status-badge ${mongoConnected ? "is-on" : "is-off"}`}>
        MONGO {mongoConnected ? "CONNECTED" : "DISCONNECTED"}
      </span>
      <span className={`status-badge ${llmConfigured ? "is-on" : "is-off"}`}>
        LLM {llmConfigured ? "READY" : "MISSING KEY"}
      </span>
    </div>
  );
}
